import React, { useState } from 'react';
import { X, Plus, Sparkles } from 'lucide-react';

export default function NewTaskModal({ projects, currentProjectId, team, onClose, onAddTask }) {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [projectId, setProjectId] = useState(currentProjectId || projects[0]?.id || '');
  const [priority, setPriority] = useState('High');
  const [assigneeId, setAssigneeId] = useState(team[0]?.id || '');
  const [estimatedHours, setEstimatedHours] = useState(16);
  const [tagInput, setTagInput] = useState('Frontend, Feature');

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!title.trim()) return;

    const newTask = {
      id: 'task-' + Date.now(),
      projectId,
      title: title.trim(),
      description: description.trim(),
      status: 'backlog',
      priority,
      assigneeId,
      dueDate: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
      tags: tagInput.split(',').map(t => t.trim()).filter(Boolean),
      estimatedHours: Number(estimatedHours),
      loggedHours: 0,
      checklist: [
        { id: 'cl-' + Date.now() + 1, text: 'Initial design review', completed: false },
        { id: 'cl-' + Date.now() + 2, text: 'Code implementation & tests', completed: false }
      ],
      comments: []
    };

    onAddTask(newTask);
    onClose();
  };

  const handleAiGenerate = () => {
    setTitle('Implement automated error tracking and Sentry SDK integration');
    setDescription('Setup global exception boundary, capture uncaught promise rejections, and configure source maps for production builds.');
    setPriority('Urgent');
    setEstimatedHours(20);
    setTagInput('DevOps, Sentry, Monitoring');
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 backdrop-blur-sm p-4 animate-fadeIn">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-xl overflow-hidden border border-slate-100 flex flex-col">
        
        <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between bg-slate-50/50">
          <div className="flex items-center space-x-2">
            <h2 className="text-lg font-bold text-slate-900">Create New Task</h2>
            <button 
              type="button"
              onClick={handleAiGenerate}
              className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-indigo-50 text-indigo-700 hover:bg-indigo-100 text-xs font-semibold rounded-lg transition"
            >
              <Sparkles className="w-3.5 h-3.5" /> AI Autofill
            </button>
          </div>
          <button onClick={onClose} className="p-2 text-slate-400 hover:text-slate-600 rounded-lg">
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="block text-xs font-semibold text-slate-600 uppercase mb-1">Project</label>
            <select 
              value={projectId} 
              onChange={(e) => setProjectId(e.target.value)}
              className="w-full text-sm font-medium bg-white border border-slate-200 rounded-xl px-3.5 py-2.5 focus:outline-none focus:ring-2 focus:ring-indigo-500"
            >
              {projects.map(p => (
                <option key={p.id} value={p.id}>{p.name} ({p.key})</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-xs font-semibold text-slate-600 uppercase mb-1">Task Title</label>
            <input 
              type="text" 
              required
              placeholder="e.g. Optimize database query performance..."
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="w-full text-sm bg-white border border-slate-200 rounded-xl px-3.5 py-2.5 focus:outline-none focus:ring-2 focus:ring-indigo-500 font-medium"
            />
          </div>

          <div>
            <label className="block text-xs font-semibold text-slate-600 uppercase mb-1">Description</label>
            <textarea 
              rows={3}
              placeholder="Provide technical specifications and acceptance criteria..."
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="w-full text-sm bg-white border border-slate-200 rounded-xl px-3.5 py-2.5 focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-semibold text-slate-600 uppercase mb-1">Priority</label>
              <select 
                value={priority}
                onChange={(e) => setPriority(e.target.value)}
                className="w-full text-sm font-medium bg-white border border-slate-200 rounded-xl px-3.5 py-2.5 focus:outline-none focus:ring-2 focus:ring-indigo-500"
              >
                <option value="Urgent">Urgent</option>
                <option value="High">High</option>
                <option value="Medium">Medium</option>
                <option value="Low">Low</option>
              </select>
            </div>

            <div>
              <label className="block text-xs font-semibold text-slate-600 uppercase mb-1">Assignee</label>
              <select 
                value={assigneeId}
                onChange={(e) => setAssigneeId(e.target.value)}
                className="w-full text-sm font-medium bg-white border border-slate-200 rounded-xl px-3.5 py-2.5 focus:outline-none focus:ring-2 focus:ring-indigo-500"
              >
                {team.map(m => (
                  <option key={m.id} value={m.id}>{m.name}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-semibold text-slate-600 uppercase mb-1">Estimated Hours</label>
              <input 
                type="number"
                value={estimatedHours}
                onChange={(e) => setEstimatedHours(e.target.value)}
                className="w-full text-sm bg-white border border-slate-200 rounded-xl px-3.5 py-2.5 focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-600 uppercase mb-1">Tags (comma separated)</label>
              <input 
                type="text"
                value={tagInput}
                onChange={(e) => setTagInput(e.target.value)}
                className="w-full text-sm bg-white border border-slate-200 rounded-xl px-3.5 py-2.5 focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
            </div>
          </div>

          <div className="pt-4 border-t border-slate-100 flex justify-end space-x-3">
            <button 
              type="button" 
              onClick={onClose}
              className="px-5 py-2.5 border border-slate-200 text-slate-700 font-medium text-sm rounded-xl hover:bg-slate-50 transition"
            >
              Cancel
            </button>
            <button 
              type="submit"
              className="px-5 py-2.5 bg-indigo-600 text-white font-medium text-sm rounded-xl hover:bg-indigo-700 transition shadow-lg shadow-indigo-600/20"
            >
              Create Task
            </button>
          </div>
        </form>

      </div>
    </div>
  );
}
